import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-service-page">
      <Header />

      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1C] mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Back
          </button>

          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-[#888] mb-12">Last updated: April 2026</p>

          <div className="space-y-10">
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Agreement to Terms</h2>
              <p className="text-[#555] leading-relaxed">
                By accessing or using the NestFlow website and purchasing our products, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our site.
              </p>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Products & Pricing</h2>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  All prices are listed in US Dollars (USD) and are subject to change without notice
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  We reserve the right to modify or discontinue any product at any time
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Product images are for illustration purposes; actual products may vary slightly
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Orders & Payment</h2>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  All orders are subject to availability and confirmation
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Payment is processed securely through Stripe at the time of purchase
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  We reserve the right to cancel any order for any reason, including suspected fraud
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  An order number will be provided upon successful payment
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Shipping</h2>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Free shipping on orders over $50
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Delivery times are estimates and not guaranteed
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  We are not responsible for delays caused by shipping carriers
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Returns & Refunds</h2>
              <p className="text-[#555] leading-relaxed">
                Returns are accepted within 14 days of delivery. For full details, please see our{' '}
                <a href="/returns" className="text-[#1A1A1C] font-semibold hover:underline">Returns & Refunds Policy</a>.
              </p>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Limitation of Liability</h2>
              <p className="text-[#555] leading-relaxed">
                NestFlow shall not be liable for any indirect, incidental, or consequential damages arising from the use of our products or website. Our total liability is limited to the amount you paid for the product in question.
              </p>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Changes to Terms</h2>
              <p className="text-[#555] leading-relaxed">
                We reserve the right to update these Terms of Service at any time. Changes will be posted on this page with an updated revision date. Continued use of the site constitutes acceptance of the revised terms.
              </p>
            </section>

            <hr className="border-[#F0F0F0]" />

            <section className="bg-[#FAFAFA] rounded-xl p-8 text-center">
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-2">Questions?</h2>
              <p className="text-[#555]">
                Contact us at{' '}
                <a href="mailto:nestflowstore@gmail.com" className="text-[#1A1A1C] font-semibold hover:underline">
                  nestflowstore@gmail.com
                </a>{' '}
                for any questions about these terms.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default TermsOfServicePage;
